// Iter-200c — Public RGPD / Privacy policy page.
import React from "react";
import { Lock, ShieldCheck, Mail } from "lucide-react";

const BRAND = {
  navy: "#1E3A5F",
  creamBg: "#FAF6EE",
  creamBg2: "#F5EBD6",
  creamBorder: "#EDE0C6",
  orange: "#D97A2A",
};

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${BRAND.creamBg} 0%, ${BRAND.creamBg2} 100%)` }} data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="BAIM" className="w-12 h-12 rounded-lg shadow-sm object-cover" />
          <div>
            <div className="font-heading font-bold text-xl" style={{ color: BRAND.navy }}>BAIM</div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>Business Agility in Motion</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>Documentation légale</div>
            <div className="text-[13px]" style={{ color: BRAND.navy }}>Politique de confidentialité</div>
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm border p-8" style={{ borderColor: BRAND.creamBorder }}>
          <div className="flex items-start gap-3 mb-6">
            <div className="rounded-full p-2 shrink-0" style={{ background: "#D1FAE5" }}>
              <ShieldCheck size={22} style={{ color: "#047857" }} />
            </div>
            <div>
              <h1 className="font-heading text-2xl" style={{ color: BRAND.navy }}>Confidentialité, RGPD & utilisation des données</h1>
              <div className="text-xs mt-1" style={{ color: BRAND.navy, opacity: 0.6 }}>Dernière mise à jour : février 2026</div>
            </div>
          </div>

          <Section title="1. Qui collecte vos données ?">
            <p>
              <strong>BAIM</strong> — Cabinet de Conseil, immatriculé au Registre du Commerce de Casablanca sous le numéro <strong>RC 710919</strong>, est responsable du traitement des données collectées via cette plateforme.
            </p>
            <Coords />
          </Section>

          <Section title="2. Quelles données traitons-nous lors d'une déclaration de paiement ?">
            <p>Lorsque vous déclarez un paiement via cette interface, BAIM collecte et conserve&nbsp;:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Vos coordonnées de déclarant</strong> : prénom, nom, adresse email professionnelle.</li>
              <li><strong>Les détails du paiement</strong> : date, montant, mode, référence (n° de virement / chèque), commentaire éventuel.</li>
              <li><strong>La pièce jointe</strong> que vous fournissez (scan de virement, swift, …) ou son équivalent texte.</li>
              <li><strong>Des métadonnées techniques minimales</strong> : adresse IP et User-Agent navigateur (à des fins exclusives d'audit anti-fraude).</li>
            </ul>
          </Section>

          <Section title="3. À quoi servent ces données ?">
            <p>Les données collectées sont strictement utilisées pour&nbsp;:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Marquer votre facture comme réglée et arrêter les relances automatiques.</li>
              <li>Constituer la piste d'audit comptable du paiement (rapprochement bancaire, justificatifs en cas de contrôle fiscal).</li>
              <li>Vous recontacter en cas de divergence (ex&nbsp;: montant non retrouvé sur l'extrait bancaire).</li>
            </ul>
            <p className="mt-3 font-medium" style={{ color: BRAND.navy }}>
              BAIM s'engage à <strong>ne jamais utiliser ces données à des fins commerciales</strong>, à <strong>ne pas les partager</strong> avec un tiers (hors obligation légale), et à <strong>ne pas les inscrire dans une newsletter</strong> sans votre consentement explicite séparé.
            </p>
          </Section>

          <Section title="4. Durée de conservation">
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Déclarations et coordonnées</strong> : 10 ans (durée légale de conservation des pièces comptables).</li>
              <li><strong>Pièces jointes</strong> : conservées 3 mois sur les serveurs de l'application puis automatiquement archivées sur un espace Google Drive privé chiffré, accessible uniquement aux administrateurs BAIM.</li>
              <li><strong>Métadonnées techniques</strong> (IP, User-Agent) : 12 mois, puis supprimées.</li>
            </ul>
          </Section>

          <Section title="5. Sécurité">
            <div className="flex items-start gap-2">
              <Lock size={16} className="mt-0.5 shrink-0" style={{ color: BRAND.navy }} />
              <div>
                <p>Les données sont stockées sur des serveurs sécurisés (chiffrement HTTPS en transit, chiffrement au repos). Le lien que vous avez utilisé pour accéder à cette page est <strong>signé cryptographiquement</strong> et <strong>exclusif à cette facture</strong> : il est impossible de l'utiliser pour accéder à une autre facture ou pour usurper l'identité d'un autre client.</p>
              </div>
            </div>
          </Section>

          <Section title="6. Vos droits (RGPD)">
            <p>Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi marocaine n°09-08, vous disposez des droits suivants&nbsp;:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Droit d'accès</strong> : obtenir une copie des données vous concernant.</li>
              <li><strong>Droit de rectification</strong> : corriger une donnée inexacte (ex&nbsp;: prénom mal orthographié).</li>
              <li><strong>Droit à l'effacement</strong> : faire supprimer vos données (sauf obligations comptables — voir §4).</li>
              <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format lisible.</li>
              <li><strong>Droit d'opposition</strong> au traitement à des fins autres que comptables.</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, écrivez à <a href="mailto:facturation@ba-inmotion.com" className="underline" style={{ color: BRAND.orange }}>facturation@ba-inmotion.com</a>. Nous répondrons sous 30 jours maximum.
            </p>
          </Section>

          <Section title="7. Cookies & traceurs">
            <p>Cette page de déclaration <strong>n'utilise aucun cookie de traçage publicitaire ni traceur tiers</strong> (Google Analytics, Facebook Pixel, etc.). Seules des données techniques essentielles à la session sont stockées temporairement dans votre navigateur.</p>
          </Section>

          <Section title="8. Réclamations">
            <p>Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la <strong>CNDP</strong> (Commission Nationale de contrôle de la protection des Données à caractère Personnel, Maroc) ou de la <strong>CNIL</strong> (France) selon votre lieu de résidence.</p>
          </Section>

          <div className="mt-8 pt-5 border-t flex items-center gap-2 text-sm" style={{ borderColor: BRAND.creamBorder, color: BRAND.navy }}>
            <Mail size={14} />
            <span>Contact DPO BAIM&nbsp;: </span>
            <a href="mailto:dpo@ba-inmotion.com" className="underline" style={{ color: BRAND.orange }}>dpo@ba-inmotion.com</a>
          </div>
        </div>

        <div className="text-center mt-6 text-[10px] font-mono" style={{ color: BRAND.navy, opacity: 0.55 }}>
          © BAIM · ba-inmotion.com · Confidentiel
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6">
      <h2 className="font-heading text-lg mb-2" style={{ color: BRAND.navy }}>{title}</h2>
      <div className="text-sm leading-relaxed" style={{ color: BRAND.navy, opacity: 0.85 }}>
        {children}
      </div>
    </div>
  );
}

function Coords() {
  return (
    <div className="mt-3 p-3 rounded-md text-[12px] font-mono" style={{ background: BRAND.creamBg, color: BRAND.navy, opacity: 0.85 }}>
      RC&nbsp;:&nbsp;710919 · ICE&nbsp;:&nbsp;003887289000010 · IF&nbsp;:&nbsp;71754267 · Patente&nbsp;:&nbsp;33304151 · CNSS&nbsp;:&nbsp;6661461<br/>
      Casablanca, Maroc · ba-inmotion.com
    </div>
  );
}
